import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { 
  WompiReportsService, 
  WompiReportPreviewDTO, 
  PlinkReportPreviewDTO, 
  ReportUploadResultDTO,
  DailyTaskStatusDTO,
  MultipleUploadResponseDTO
} from '../../services/wompi-reports.service';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-report-upload',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './report-upload.component.html',
  styleUrl: './report-upload.component.scss'
})
export class ReportUploadComponent implements OnInit {
  // Estado de la tarea diaria
  dailyTaskStatus: DailyTaskStatusDTO | null = null;
  
  // Estados de carga
  isLoadingWompi = false;
  isLoadingPlink = false;
  isUploadingWompi = false;
  isUploadingPlink = false;
  
  // Archivos seleccionados
  wompiFiles: File[] = [];
  plinkFiles: File[] = [];
  
  // Previews
  wompiPreview: WompiReportPreviewDTO | null = null;
  plinkPreview: PlinkReportPreviewDTO | null = null;
  
  // Respuesta de multi-upload
  wompiMultiUploadResponse: MultipleUploadResponseDTO | null = null;
  plinkMultiUploadResponse: MultipleUploadResponseDTO | null = null;
  
  // Estados de drag & drop
  isDraggingWompi = false;
  isDraggingPlink = false;
  
  // Resultados de carga
  wompiResult: ReportUploadResultDTO | null = null;
  plinkResult: ReportUploadResultDTO | null = null;
  
  // Control local de carga exitosa (bloqueo inmediato)
  wompiUploadedLocally = false;
  plinkUploadedLocally = false;

  constructor(
    private wompiReportsService: WompiReportsService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadDailyTaskStatus();
  }

  loadDailyTaskStatus(): void {
    this.wompiReportsService.getDailyTaskStatus().subscribe({
      next: (status) => {
        this.dailyTaskStatus = status;
      },
      error: (error) => {
        console.error('Error al cargar estado de tarea diaria:', error);
      }
    });
  }

  // ============== Eventos de Drag & Drop ==============

  onDragOver(event: DragEvent, type: 'wompi' | 'plink'): void {
    event.preventDefault();
    event.stopPropagation();
    if (type === 'wompi') {
      this.isDraggingWompi = true;
    } else {
      this.isDraggingPlink = true;
    }
  }

  onDragLeave(event: DragEvent, type: 'wompi' | 'plink'): void {
    event.preventDefault();
    event.stopPropagation();
    if (type === 'wompi') {
      this.isDraggingWompi = false;
    } else {
      this.isDraggingPlink = false;
    }
  }

  onDrop(event: DragEvent, type: 'wompi' | 'plink'): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (type === 'wompi') {
      this.isDraggingWompi = false;
    } else {
      this.isDraggingPlink = false;
    }

    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      if (type === 'wompi') {
        this.handleWompiFiles(Array.from(droppedFiles));
      } else {
        this.handlePlinkFiles(Array.from(droppedFiles));
      }
    }
  }

  // ============== Selección de archivos ==============

  onWompiFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleWompiFiles(Array.from(input.files));
    }
  }

  onPlinkFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handlePlinkFiles(Array.from(input.files));
    }
  }

  handleWompiFiles(files: File[]): void {
    const invalidFiles = files.filter(f => f.name.split('.').pop()?.toLowerCase() !== 'csv');
    if (invalidFiles.length > 0) {
      this.toastService.showToast('error', `Los siguientes archivos no son CSV: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    this.wompiFiles = [...this.wompiFiles, ...files];
    this.wompiPreview = null;
    this.wompiResult = null;
    this.wompiMultiUploadResponse = null;
  }

  removeWompiFile(index: number): void {
    this.wompiFiles.splice(index, 1);
  }

  handlePlinkFiles(files: File[]): void {
    const invalidFiles = files.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return ext !== 'xlsx' && ext !== 'xls';
    });
    if (invalidFiles.length > 0) {
      this.toastService.showToast('error', `Los siguientes archivos no son Excel: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    this.plinkFiles = [...this.plinkFiles, ...files];
    this.plinkPreview = null;
    this.plinkResult = null;
    this.plinkMultiUploadResponse = null;
  }

  removePlinkFile(index: number): void {
    this.plinkFiles.splice(index, 1);
  }

  // ============== Previews ==============

  previewWompiReport(): void {
    // Preview solo para archivos individuales (backward compatibility)
    if (this.wompiFiles.length !== 1) return;

    this.isLoadingWompi = true;
    this.wompiReportsService.previewWompiReport(this.wompiFiles[0]).subscribe({
      next: (preview) => {
        this.wompiPreview = preview;
        this.isLoadingWompi = false;
        
        if (preview.hasErrors) {
          this.toastService.showToast('warning', 'El archivo tiene algunos problemas. Revise los detalles.');
        }
      },
      error: (error) => {
        this.isLoadingWompi = false;
        this.toastService.showToast('error', 'Error al procesar el archivo: ' + error.message);
      }
    });
  }

  previewPlinkReport(): void {
    if (this.plinkFiles.length !== 1) return;

    this.isLoadingPlink = true;
    this.wompiReportsService.previewPlinkReport(this.plinkFiles[0]).subscribe({
      next: (preview) => {
        this.plinkPreview = preview;
        this.isLoadingPlink = false;
        
        if (preview.hasErrors) {
          this.toastService.showToast('warning', 'El archivo tiene algunos problemas. Revise los detalles.');
        }
      },
      error: (error) => {
        this.isLoadingPlink = false;
        this.toastService.showToast('error', 'Error al procesar el archivo: ' + error.message);
      }
    });
  }

  // ============== Uploads ==============

  uploadWompiReport(): void {
    if (this.wompiFiles.length === 0) return;

    this.isUploadingWompi = true;
    this.wompiMultiUploadResponse = null;

    this.wompiReportsService.uploadWompiReportMultiple(this.wompiFiles).subscribe({
      next: (response) => {
        this.wompiMultiUploadResponse = response;
        this.isUploadingWompi = false;
        this.wompiUploadedLocally = true;
        this.toastService.showToast('success', response.message);
        this.loadDailyTaskStatus();
        this.clearWompiFile();
      },
      error: (error) => {
        this.isUploadingWompi = false;
        const errorMsg = error.error?.message || error.message || 'Error desconocido';
        this.toastService.showToast('error', 'Error al encolar los archivos: ' + errorMsg);
      }
    });
  }

  uploadPlinkReport(): void {
    if (this.plinkFiles.length === 0) return;

    this.isUploadingPlink = true;
    this.plinkMultiUploadResponse = null;

    this.wompiReportsService.uploadPlinkReportMultiple(this.plinkFiles).subscribe({
      next: (response) => {
        this.plinkMultiUploadResponse = response;
        this.isUploadingPlink = false;
        this.plinkUploadedLocally = true;
        this.toastService.showToast('success', response.message);
        this.loadDailyTaskStatus();
        this.clearPlinkFiles();
      },
      error: (error) => {
        this.isUploadingPlink = false;
        const errorMsg = error.error?.message || error.message || 'Error desconocido';
        this.toastService.showToast('error', 'Error al encolar los archivos: ' + errorMsg);
      }
    });
  }

  // ============== Helpers ==============

  clearWompiFile(): void {
    this.wompiFiles = [];
    this.wompiPreview = null;
  }

  clearPlinkFiles(): void {
    this.plinkFiles = [];
    this.plinkPreview = null;
  }

  isTaskComplete(): boolean {
    const wompiDone = this.dailyTaskStatus?.wompiUploaded || this.wompiUploadedLocally;
    const plinkDone = this.dailyTaskStatus?.plinkUploaded || this.plinkUploadedLocally;
    return wompiDone && plinkDone;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-CO');
  }

  formatTime(dateString: string | undefined | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  }

  formatCurrency(value: number): string {
    if (value === null || value === undefined) return '$ 0';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  }

  formatCurrencyShort(value: number): string {
    if (value === null || value === undefined) return '$ 0';
    if (value >= 1000000) {
      return '$ ' + (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return '$ ' + (value / 1000).toFixed(0) + 'K';
    }
    return '$ ' + value.toFixed(0);
  }

  // ============== Cálculos de totales ==============

  getTotalWompiFilesSize(): number {
    return this.wompiFiles.reduce((sum, file) => sum + file.size, 0);
  }

  getTotalPlinkFilesSize(): number {
    return this.plinkFiles.reduce((sum, file) => sum + file.size, 0);
  }

  getTotalWompiAmount(): number {
    if (!this.wompiPreview?.records) return 0;
    return this.wompiPreview.records.reduce((sum, record) => sum + (record.amount || 0), 0);
  }

  getTotalPlinkValue(): number {
    if (!this.plinkPreview?.records) return 0;
    return this.plinkPreview.records.reduce((sum, record) => sum + (record.totalValue || 0), 0);
  }

  getTotalPlinkCommission(): number {
    if (!this.plinkPreview?.records) return 0;
    return this.plinkPreview.records.reduce((sum, record) => {
      const commission = (record.commissionValue || 0) + 
                         (record.reteFuenteValue || 0) + 
                         (record.reteIVAValue || 0) + 
                         (record.reteICAValue || 0) + 
                         (record.provisionValue || 0);
      return sum + commission;
    }, 0);
  }

  getTotalPlinkNet(): number {
    if (!this.plinkPreview?.records) return 0;
    return this.plinkPreview.records.reduce((sum, record) => sum + (record.netValue || 0), 0);
  }
}

